import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import PHASE_INFO from '../data/hormonalCyclePhases';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mensajes de transición por fase. Lenguaje condicional.
const PHASE_TRANSITION_MESSAGES = {
  menstrual: 'Estás entrando en fase menstrual. Es posible que notes menos energía o necesites más descanso.',
  folicular: 'Estás entrando en fase folicular. Tu energía suele ir subiendo — buen momento para retomar intensidad.',
  ovulatoria: 'Estás entrando en fase ovulatoria. Suele ser el pico de energía del ciclo.',
  lutea_temprana: 'Estás entrando en fase lútea temprana. Tu energía empieza a estabilizarse.',
  lutea_tardia: 'Estás entrando en fase lútea tardía. Es posible que notes más hambre, retención o cambios de ánimo.'
};

// Modal suave que aparece UNA vez al detectar un cambio de fase.
// No bloquea el dashboard (es dismisible).
// Respeta la configuración de recordatorios del usuario.
export default function HormonalCyclePhaseModal() {
  const navigate = useNavigate();
  const [phaseData, setPhaseData] = useState(null);
  const [phaseKey, setPhaseKey] = useState(null);
  const [show, setShow] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('deisycfit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const checkPhaseTransition = async () => {
      try {
        const res = await axios.get(`${API}/hormonal-cycle/status`, { headers: authHeaders() });
        const data = res.data;

        // Reglas para NO mostrar:
        // 1) módulo deshabilitado
        // 2) recordatorios apagados
        // 3) hide_all activo
        // 4) no hay datos de ciclo (no ha hecho setup)
        if (!data?.enabled) return;
        if (!data?.reminders_enabled) return;
        if (data?.reminders_config?.hide_all) return;
        if (!data?.current_estimated_phase) return;

        const currentPhase = data.current_estimated_phase;
        const cfg = data.reminders_config || {};

        // Respetar config por fase
        const phaseEnabled = (
          (currentPhase === 'menstrual' && cfg.menstrual_start !== false) ||
          (currentPhase === 'ovulatoria' && cfg.ovulation !== false) ||
          ((currentPhase === 'lutea_temprana' || currentPhase === 'lutea_tardia') && cfg.luteal_phase !== false) ||
          (currentPhase === 'folicular' && cfg.menstrual_start !== false)  // mostrar al salir de menstrual
        );
        if (!phaseEnabled) return;

        // Siempre mostrar el card de la fase actual (entrando, en el medio o saliendo)
        setPhaseKey(currentPhase);
        setPhaseData(PHASE_INFO[currentPhase]);
        setShow(true);
      } catch (e) {
        // silent
      }
    };

    checkPhaseTransition();
  }, []);

  const markSeen = () => {
    if (phaseKey) {
      localStorage.setItem('hormonal_last_shown_phase', phaseKey);
    }
    setShow(false);
  };

  const handleViewSuggestions = () => {
    markSeen();
    navigate('/mi-ciclo-hormonal');
  };

  const handleHideReminders = async () => {
    try {
      await axios.put(`${API}/hormonal-cycle/reminders`, {
        menstrual_start: false,
        luteal_phase: false,
        ovulation: false,
        daily_checkin: false,
        hide_all: true
      }, { headers: authHeaders() });
    } catch (e) { /* silent */ }
    markSeen();
  };

  if (!show || !phaseData) return null;

  const transitionMessage = PHASE_TRANSITION_MESSAGES[phaseKey] || phaseData.description;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.4 }}
        className="rounded-2xl p-5 md:p-6 mb-4 relative"
        style={{
          background: `linear-gradient(135deg, ${phaseData.color}25, ${phaseData.color}08)`,
          border: `1px solid ${phaseData.color}40`
        }}
        data-testid="phase-transition-modal"
      >
        <div className="flex items-start gap-4">
          <div className="text-4xl flex-shrink-0">{phaseData.emoji}</div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: phaseData.color }}>
              Mi ciclo hormonal
            </p>
            <h3 className="text-white font-bold text-base md:text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif' }} data-testid="phase-transition-title">
              {phaseData.name}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {transitionMessage}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleViewSuggestions}
                size="sm"
                className="text-white text-xs"
                style={{ background: `linear-gradient(135deg, ${phaseData.color}, var(--brand-primary))` }}
                data-testid="view-suggestions-btn"
              >
                Ver sugerencias
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={markSeen}
                className="border-white/20 text-gray-300 hover:bg-white/5"
                data-testid="phase-ok-btn"
              >
                Ok
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={markSeen}
                className="text-gray-400 hover:text-gray-200 text-xs"
                data-testid="phase-omit-btn"
              >
                Omitir
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleHideReminders}
                className="text-gray-500 hover:text-gray-300 text-xs"
                data-testid="phase-hide-reminders-btn"
              >
                No volver a mostrar recordatorios
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
